import { CommonModule, DatePipe } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { ProjectWorkflowService } from "../../../core/services/project-workflow.service";
import { TaskManagementService } from "../../../core/services/task-management.service";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";
import { GetAllAssignedUsers } from "../../../core/models/task-management/getAllAssignedUsersDto";
import { ProjectWorkflowDetail } from "../../../core/models/task-management/getProjectWorkFlowDetailDto";
import { ProjectWorkflowTags } from "../../../core/models/task-management/getTagsByProjectWorkflowList";
import { PriorityTypeEnums } from "../../../shared/enums/priorityTypeEnum";
import { EnumSalesTaskStatus } from "../../../shared/enums/salesTaskStatus";
import { WorkflowStatusEnum } from "../../../shared/enums/workflowStatusEnum";
import { SelectModule } from "primeng/select";
import { DatePickerModule } from "primeng/datepicker";
import { InputTextModule } from "primeng/inputtext";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { ButtonModule } from "primeng/button";
import { NumberOnlyDirective } from "../../../core/directives/number-only.directive";

interface FilterFormValues {
  title?: string;
  state?: string;
  linkedProject?: string;
  dueDate?: string;
  assignedTo?: string;
  priority?: string;
  remindMe?: string;
  jobName?: string;
  selectTag?: string;
  duration?: string;
}
@UntilDestroy()
@Component({
  selector: "jni-task-filter-popup",
  templateUrl: "./task-filter-popup.component.html",
  styleUrls: ["./task-filter-popup.component.scss"],
  imports: [
    ReactiveFormsModule,
    SelectModule,
    DatePickerModule,
    CommonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    NumberOnlyDirective,
  ],
})
export class TaskFilterPopupComponent implements OnInit, OnChanges {
  filterForm!: FormGroup;
  productionStatusEnum = WorkflowStatusEnum;
  salesStatusEnum = EnumSalesTaskStatus;
  priorityTypeList!: string[];
  priorityEnum = PriorityTypeEnums;
  projectLists: ProjectWorkflowDetail[] = [];
  stateList!: string[];
  tagList!: ProjectWorkflowTags[];
  assignedUserList!: GetAllAssignedUsers[];
  payloadFilter!: GenericFilterRequestDto[];
  @Output() showClearFilter = new EventEmitter<boolean>();
  @Output() closeFilterPopUp = new EventEmitter<void>();
  @Output() addFilterValues = new EventEmitter<GenericFilterRequestDto[]>();
  @Output() assignedUsersEmitter = new EventEmitter<GetAllAssignedUsers[]>();
  @Input() taskType!: number;
  workflowStatusList!: Record<string, number>;
  taskStatusKeyPairsSales: Record<string, number> = {
    New: EnumSalesTaskStatus.New,
    Blocked: EnumSalesTaskStatus.Blocked,
    "In Progress": EnumSalesTaskStatus.InProgress,
    "In Review": EnumSalesTaskStatus.InReview,
    Closed: EnumSalesTaskStatus.Closed,
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _taskManagementService: TaskManagementService,
    private _toastr: JniToastrService,
    private _datePipe: DatePipe,
    private _workflowService: ProjectWorkflowService
  ) {
    this.initializeFilterForm();
  }

  async ngOnInit(): Promise<void> {
    await this.getWorkflowStatusList();
    await this.getAllTags();
    await this.getAllAssignedUsers();
    this.priorityTypeList = this.prioirtyKeys();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["taskType"]) {
      const taskType = changes["taskType"].currentValue;
      const bindStateList = () => {
        if (taskType === 1 || taskType === "1") {
          this.stateList = [...Object.keys(this.taskStatusKeyPairsSales)];
        } else if (taskType === 2 || taskType === "2") {
          this.stateList = [...Object.keys(this.workflowStatusList)];
        } else if (taskType === 0 || taskType === "0") {
          this.stateList = [
            ...Object.keys(this.taskStatusKeyPairsSales),
            ...Object.keys(this.workflowStatusList),
          ];
        }
      };

      if (
        !this.workflowStatusList ||
        Object.keys(this.workflowStatusList).length === 0
      ) {
        this.getWorkflowStatusList().then(() => bindStateList());
      } else {
        bindStateList();
      }
    }
  }

  getWorkflowStatusList(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._workflowService.getWorkflowStatusList().subscribe({
        next: res => {
          this.workflowStatusList = {};
          const statusEnums: Record<string, number> = {};
          if (res?.data) {
            res.data.forEach((status: { status: string; id: number }) => {
              if (status.id !== 9 && status.id !== 1) {
                statusEnums[status.status] = status.id;
              }
            });
            this.workflowStatusList = statusEnums;
          }
          resolve();
        },
        error: err => {
          this._toastr.error("Error", "Unable to get Workflow Status List");
          reject(err);
        },
      });
    });
  }

  prioirtyKeys(): string[] {
    const keys = Object.keys(this.priorityEnum);
    return keys.splice(keys.length / 2, keys.length - 1);
  }

  stateKeys(): string[] {
    const sales = Object.keys(this.productionStatusEnum);
    const production = Object.keys(this.salesStatusEnum);
    return [
      ...sales.slice(sales.length / 2, sales.length - 1),
      ...production.slice(production.length / 2, production.length - 1),
    ];
  }

  getPriorityId(key: keyof typeof PriorityTypeEnums) {
    return String(this.priorityEnum[key] ?? "");
  }

  getStatusId(key: keyof typeof EnumSalesTaskStatus) {
    const value = this.salesStatusEnum[key];
    if (value) {
      return String(value);
    } else {
      const newKey = key as unknown as keyof typeof WorkflowStatusEnum;
      return String(this.productionStatusEnum[newKey] ?? "");
    }
  }

  initializeFilterForm() {
    this.filterForm = this._formBuilder.group({
      title: [""],
      state: [""],
      linkedProject: [""],
      dueDate: [""],
      assignedTo: [""],
      priority: [""],
      remindMe: [""],
      jobName: [""],
      selectTag: [""],
      duration: [""],
    });
  }

  clearFilters() {
    this.filterForm.reset();
    this.showClearFilter.emit(false);
    //this.addFilterValues.emit([]);
    //this.showClearFilter.emit(false);
  }

  getAllTags(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._taskManagementService
        .getAllTags()
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            if (res?.data) {
              this.tagList = res.data;
            }
            resolve();
          },
          error: res => {
            this._toastr.error("Error", "Error Fetching All Tags");
            reject(res);
          },
        });
    });
  }

  getAllAssignedUsers(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._taskManagementService
        .getTeamUsers()
        .pipe(untilDestroyed(this))
        .subscribe({
          next: res => {
            if (res?.data) {
              this.assignedUserList = res.data.map(users => {
                const assignedUser: GetAllAssignedUsers = {
                  name: users.name,
                  personalNumber: users.personalNumber,
                  title: users.title,
                  locationName: "",
                  loginEmail: "",
                };
                return assignedUser;
              });
              this.assignedUsersEmitter.emit(this.assignedUserList);
            }
            resolve();
          },
          error: res => {
            this._toastr.error("Error", "Error Fetching All Assigned Users");
            reject(res);
          },
        });
    });
  }

  getFormFilteredValues() {
    const formValues = this.filterForm.value;
    const filteredValues: Record<string, string | number | boolean | null> = {};
    for (const key in formValues) {
      if (formValues[key]) {
        filteredValues[key] = formValues[key];
      }
    }
    if (Object.keys(filteredValues).length !== 0) {
      this.generateFilterConfiguration(formValues, filteredValues);
    }
    return filteredValues;
  }

  getIdByTitle(key: number) {
    return PriorityTypeEnums[key]?.toString();
  }

  generateFilterConfiguration(
    event: FilterFormValues,
    filteredValues: Record<string, string | number | boolean | null>
  ) {
    const eventFilterArray = Object.keys(event || {});
    const filter: GenericFilterRequestDto[] = eventFilterArray.map(x => {
      return {
        columnName: x || "",
        matchMode: "startsWith",
        tableName: "",
        value: filteredValues[x] ?? "",
      } as GenericFilterRequestDto;
    });

    filter.forEach(x => {
      if (x.columnName == "jobName") {
        x.columnName = "JobName";
        x.tableName = "workflowheaders";
      }
      if (x.columnName == "title") {
        x.columnName = "Title";
        x.tableName = "UserTasks";
      }
      if (x.columnName == "dueDate") {
        x.columnName = "DueDate";
        x.tableName = "UserTasks";
        x.value = this._datePipe.transform(x.value, "yyyy-MM-dd") ?? "";
      }
      if (x.columnName == "assignedTo") {
        x.columnName = "PersonnelNumber";
        x.tableName = "taskassignmentsbyname";
      }
      if (x.columnName == "priority") {
        x.columnName = "PriorityType";
        x.tableName = "Usertasks";
        x.value =
          this.getPriorityId(x.value as keyof typeof PriorityTypeEnums) ?? "";
      }
      if (x.columnName == "state") {
        x.columnName = "Status";
        x.tableName = "TaskStatus";
        x.value =
          this.getStatusId(
            x.value.replace(" ", "") as keyof typeof EnumSalesTaskStatus
          ) ?? "";
      }
      if (x.columnName == "linkedProject") {
        x.columnName = "Id";
        x.tableName = "workflowheaders";
      }
      if (x.columnName == "selectTag") {
        x.columnName = "Name";
        x.tableName = "Tags";
      }
      if (x.columnName == "remindMe") {
        x.columnName = "RemindMe";
        x.tableName = "UserTasks";
      }
    });
    this.showClearFilter.emit(true);
    this.addFilterValues.emit(filter);
  }

  close() {
    this.closeFilterPopUp.emit();
  }
}
